import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import {
  Building2,
  Eye,
  FileText,
  Image as ImageIcon,
  Palette,
  Save,
  Trash2,
  Type,
  Upload,
} from "lucide-react";
import {
  brandSettingsApi,
  type BrandSettingsDto,
  type UpdateBrandSettingsPayload,
} from "@/api/brandSettings";
import { Button, FormField, Input, Label, Textarea } from "@/design-system/components/ui";
import { PageHeader } from "@/components/layout/PageHeader";
import { BrandTemplateSection } from "./BrandTemplateSection";
import { formatDateTimeInTz, useDateFormatter } from "@/utils/datetime";

const HEX_RE = /^#[0-9A-Fa-f]{6}$/;
const HEADER_FONT_OPTIONS = ["Montserrat", "Inter", "Roboto", "Playfair Display"];
const BODY_FONT_OPTIONS = ["Montserrat", "Inter", "Roboto", "Open Sans"];
const MONO_FONT_OPTIONS = ["JetBrains Mono", "Fira Code", "Source Code Pro", "Roboto Mono"];

export function BrandIdentityPage() {
  const qc = useQueryClient();
  const settings = useQuery({
    queryKey: ["brand-settings"],
    queryFn: () => brandSettingsApi.get(),
  });

  if (settings.isLoading) {
    return (
      <>
        <PageHeader eyebrow="Admin" title="Brand identity" description="Loading brand settings..." />
      </>
    );
  }

  if (!settings.data) {
    return (
      <>
        <PageHeader eyebrow="Admin" title="Brand identity" />
        <div className="text-small text-status-error" role="alert">
          Could not load brand settings.
        </div>
      </>
    );
  }

  return <BrandIdentityForm initial={settings.data} onSaved={() => qc.invalidateQueries({ queryKey: ["brand-settings"] })} />;
}

interface FormProps {
  initial: BrandSettingsDto;
  onSaved: () => void;
}

function BrandIdentityForm({ initial, onSaved }: FormProps) {
  const [form, setForm] = useState<UpdateBrandSettingsPayload>(() => toPayload(initial));
  const [error, setError] = useState<string | null>(null);
  const [previewText, setPreviewText] = useState<string | null>(null);
  const [current, setCurrent] = useState<BrandSettingsDto>(initial);
  const { tz } = useDateFormatter();

  useEffect(() => {
    setForm(toPayload(initial));
    setCurrent(initial);
  }, [initial]);

  const colorErrors = useMemo(
    () => ({
      primary: !HEX_RE.test(form.primaryColor),
      secondary: !HEX_RE.test(form.secondaryColor),
      accent: !HEX_RE.test(form.accentColor),
    }),
    [form.primaryColor, form.secondaryColor, form.accentColor],
  );
  const hasColorError = colorErrors.primary || colorErrors.secondary || colorErrors.accent;

  const save = useMutation({
    mutationFn: () => brandSettingsApi.update(form),
    onSuccess: (dto) => {
      setError(null);
      setCurrent(dto);
      setForm(toPayload(dto));
      onSaved();
    },
    onError: (e: Error) => setError(e.message ?? "Failed to save brand settings."),
  });

  const preview = useMutation({
    mutationFn: () => brandSettingsApi.preview(),
    onSuccess: (dto) => setPreviewText(dto.text),
    onError: (e: Error) => setError(e.message ?? "Failed to load preview."),
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (hasColorError || !form.companyName.trim()) {
      setError("Please correct the highlighted fields before saving.");
      return;
    }
    save.mutate();
  };

  const setField = <K extends keyof UpdateBrandSettingsPayload>(key: K, value: UpdateBrandSettingsPayload[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  return (
    <form onSubmit={onSubmit} className="grid gap-space-4 pb-space-7">
      <PageHeader
        eyebrow="Admin"
        title="Brand identity"
        description="Configure how Phoenix Capital documents and Claude-generated content are branded."
        actions={
          <Button type="submit" loading={save.isPending} disabled={hasColorError}>
            <Save size={14} strokeWidth={1.5} aria-hidden /> Save changes
          </Button>
        }
      />

      {error && (
        <div className="rounded-md border border-status-error/40 bg-status-error/5 px-space-3 py-space-2 text-small text-status-error" role="alert">
          {error}
        </div>
      )}

      <Section title="Company information" icon={Building2}>
        <div className="grid gap-space-3 md:grid-cols-2">
          <FormField label="Company name" required htmlFor="companyName">
            <Input
              id="companyName"
              value={form.companyName}
              onChange={(e) => setField("companyName", e.target.value)}
              required
            />
          </FormField>
          <FormField label="Tagline" htmlFor="tagline">
            <Input
              id="tagline"
              value={form.tagline ?? ""}
              onChange={(e) => setField("tagline", e.target.value || null)}
            />
          </FormField>
          <FormField label="Address" htmlFor="address" className="md:col-span-2">
            <Input
              id="address"
              value={form.companyAddress ?? ""}
              onChange={(e) => setField("companyAddress", e.target.value || null)}
            />
          </FormField>
          <FormField label="Phone" htmlFor="phone">
            <Input
              id="phone"
              value={form.companyPhone ?? ""}
              onChange={(e) => setField("companyPhone", e.target.value || null)}
            />
          </FormField>
          <FormField label="Email" htmlFor="email">
            <Input
              id="email"
              type="email"
              value={form.companyEmail ?? ""}
              onChange={(e) => setField("companyEmail", e.target.value || null)}
            />
          </FormField>
          <FormField label="Website" htmlFor="website" className="md:col-span-2">
            <Input
              id="website"
              type="url"
              value={form.companyWebsite ?? ""}
              onChange={(e) => setField("companyWebsite", e.target.value || null)}
            />
          </FormField>
        </div>
      </Section>

      <Section title="Visual identity" icon={Palette}>
        <div className="grid gap-space-4 md:grid-cols-2">
          <AssetUpload
            label="Logo"
            helper="PNG, JPG, or SVG. Max 5MB."
            accept="image/png,image/jpeg,image/svg+xml"
            currentName={current.logoFileName}
            currentUrl={current.logoUrl}
            uploader={(file) => brandSettingsApi.uploadLogo(file)}
            remover={() => brandSettingsApi.deleteLogo()}
            onChange={setCurrent}
          />
          <AssetUpload
            label="Favicon"
            helper="PNG or ICO. Max 1MB."
            accept="image/png,image/x-icon,image/vnd.microsoft.icon"
            currentName={current.faviconFileName}
            currentUrl={current.faviconUrl}
            uploader={(file) => brandSettingsApi.uploadFavicon(file)}
            remover={() => brandSettingsApi.deleteFavicon()}
            onChange={setCurrent}
          />
        </div>

        <div className="grid gap-space-3 md:grid-cols-3 mt-space-4">
          <ColorField
            label="Primary"
            value={form.primaryColor}
            invalid={colorErrors.primary}
            onChange={(v) => setField("primaryColor", v)}
          />
          <ColorField
            label="Secondary"
            value={form.secondaryColor}
            invalid={colorErrors.secondary}
            onChange={(v) => setField("secondaryColor", v)}
          />
          <ColorField
            label="Accent"
            value={form.accentColor}
            invalid={colorErrors.accent}
            onChange={(v) => setField("accentColor", v)}
          />
        </div>

        <div className="mt-space-4">
          <Label className="mb-space-2 block">Palette preview</Label>
          <div className="flex h-16 overflow-hidden rounded-md border border-border-light">
            <div className="flex-1" style={{ background: form.primaryColor }} />
            <div className="flex-1" style={{ background: form.secondaryColor }} />
            <div className="flex-1" style={{ background: form.accentColor }} />
          </div>
        </div>
      </Section>

      <Section title="Typography" icon={Type}>
        <div className="grid gap-space-3 md:grid-cols-3">
          <FontField label="Header font" options={HEADER_FONT_OPTIONS} value={form.headerFont} onChange={(v) => setField("headerFont", v)} />
          <FontField label="Body font" options={BODY_FONT_OPTIONS} value={form.bodyFont} onChange={(v) => setField("bodyFont", v)} />
          <FontField label="Mono font" options={MONO_FONT_OPTIONS} value={form.monoFont} onChange={(v) => setField("monoFont", v)} />
        </div>
      </Section>

      <Section title="Document generation" icon={ImageIcon}>
        <div className="grid gap-space-3">
          <FormField label="Brand voice description" htmlFor="brandVoice" helperText="Guidance Claude sees on every generation.">
            <Textarea
              id="brandVoice"
              rows={3}
              value={form.brandVoiceDescription ?? ""}
              onChange={(e) => setField("brandVoiceDescription", e.target.value || null)}
            />
          </FormField>
          <FormField label="Legal footer" htmlFor="legalFooter">
            <Input
              id="legalFooter"
              value={form.legalFooter ?? ""}
              onChange={(e) => setField("legalFooter", e.target.value || null)}
            />
          </FormField>
          <FormField label="Confidentiality notice" htmlFor="confidentiality">
            <Textarea
              id="confidentiality"
              rows={2}
              value={form.confidentialityNotice ?? ""}
              onChange={(e) => setField("confidentialityNotice", e.target.value || null)}
            />
          </FormField>
          <FormField
            label="Custom document header HTML"
            htmlFor="documentHeaderHtml"
            helperText="Optional. Rendered above generated documents."
          >
            <Textarea
              id="documentHeaderHtml"
              rows={4}
              className="font-mono text-caption"
              value={form.documentHeaderHtml ?? ""}
              onChange={(e) => setField("documentHeaderHtml", e.target.value || null)}
            />
          </FormField>
        </div>
      </Section>

      <Section title="Prequalification" icon={FileText}>
        <label className="flex items-start gap-space-2 text-small">
          <input
            type="checkbox"
            className="mt-space-1"
            checked={form.nativeAnalysisDocsEnabled}
            onChange={(e) => setField("nativeAnalysisDocsEnabled", e.target.checked)}
          />
          <span>
            Enable private analysis documents for Phoenix-native prequalification
            submissions
            <span className="block text-caption text-gray-mid mt-space-1">
              The two prospect-only analysis documents (Buyer &amp; Seller). Off by
              default; turn on once the AI-disclosure wording clears legal. Partner
              portals have their own toggle on each partner brand.
            </span>
          </span>
        </label>

        <FunnelCopySignpost />
      </Section>

      {/* LLM & Reports v3.2 §10.3 — exemplar Word template upload UI.
          Self-contained section: owns its own queries, mutations, and
          toasts. The page's parent save/dirty flow doesn't track this
          section because the upload endpoint is independently
          transactional. */}
      <BrandTemplateSection />

      <Section title="Branding guide preview" icon={Eye}>
        <p className="text-small text-gray-mid mb-space-3">
          This is exactly what Claude receives as <code className="font-mono">{`{{branding_guide}}`}</code> on every generation.
          Click Preview to render the current saved values.
        </p>
        <div className="flex items-center gap-space-2 mb-space-3">
          <Button type="button" variant="secondary" size="sm" onClick={() => preview.mutate()} loading={preview.isPending}>
            <Eye size={14} strokeWidth={1.5} aria-hidden /> Preview branding guide
          </Button>
          {previewText !== null && (
            <span className="text-caption text-gray-mid">Reflects last saved values, not unsaved edits.</span>
          )}
        </div>
        {previewText !== null && (
          <pre className="rounded-md border border-border-light bg-white-warm p-space-3 text-caption font-mono text-onyx whitespace-pre-wrap">
            {previewText}
          </pre>
        )}
      </Section>

      <div className="sticky bottom-0 -mx-space-3 px-space-3 py-space-3 bg-white-pure border-t border-border-light flex items-center justify-between">
        <div className="text-caption text-gray-mid">
          {current.modifiedAt
            ? `Last updated by ${current.modifiedByDisplayName ?? "an admin"} on ${formatDate(current.modifiedAt, tz)}`
            : "Not yet edited."}
        </div>
        <Button type="submit" loading={save.isPending} disabled={hasColorError}>
          <Save size={14} strokeWidth={1.5} aria-hidden /> Save changes
        </Button>
      </div>
    </form>
  );
}

/**
 * A pointer to the funnel's copy editor — NOT an editor.
 *
 * This exists because looking for the funnel's headings on the Brand Identity page
 * is the obvious thing to do and, until this shipped, found nothing. The words live
 * in the Document Language editor with the rest of the prequalification prose, and
 * one editor with a door on it beats a second editing surface here.
 *
 * 🔴 <b>Gated on the EDITOR's own permission, not this page's.</b> Brand Identity is
 * `System.ManageBrandSettings` and the editor is
 * `Prequalification.ManageDocuments` — two different grants, held by different
 * people. A reader with the first and not the second sees the explanation and no
 * link, rather than a link that 403s them. That is also why this must be walked as
 * somebody WITHOUT the editor permission: an admin holds both and would see the
 * link either way.
 */
function FunnelCopySignpost() {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission("Prequalification.ManageDocuments");

  return (
    <div className="mt-space-4 pt-space-4 border-t border-border-light">
      <h3 className="font-display font-bold text-body text-onyx m-0">
        Funnel headings &amp; marketing copy
      </h3>
      <p className="text-caption text-gray-mid mt-space-1 mb-space-2">
        The public prequalification funnel&apos;s headings, its landing page, and every
        step&apos;s question live in the Document Language editor, alongside the
        funnel&apos;s fine print — not on this page. Edits publish without a deploy.
      </p>
      {canEdit ? (
        <Link
          to="/deal-flow/prequalification/documents?doc=funnel-copy"
          className="text-caption text-gold-shadow hover:underline"
        >
          Open the Document Language editor →
        </Link>
      ) : (
        <p className="text-caption text-gray-mid m-0 italic">
          You do not have access to the Document Language editor.
        </p>
      )}
    </div>
  );
}

function Section({
  title,
  icon: Icon,
  children,
}: {
  title: string;
  icon: React.ComponentType<{ size?: number; strokeWidth?: number; "aria-hidden"?: boolean }>;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-md border border-border-light bg-white-pure p-space-4">
      <div className="flex items-center gap-space-2 mb-space-3">
        <Icon size={18} strokeWidth={1.5} aria-hidden />
        <h2 className="font-display font-bold text-h4 text-onyx m-0">{title}</h2>
      </div>
      {children}
    </section>
  );
}

function ColorField({
  label, value, invalid, onChange,
}: {
  label: string;
  value: string;
  invalid: boolean;
  onChange: (v: string) => void;
}) {
  return (
    <FormField
      label={label}
      htmlFor={`color-${label.toLowerCase()}`}
      error={invalid ? "Must be a 6-digit hex (e.g. #C9A961)." : undefined}
    >
      <div className="flex gap-space-2">
        <input
          type="color"
          aria-label={`${label} color picker`}
          className="h-10 w-12 cursor-pointer rounded-md border border-border-light p-1"
          value={HEX_RE.test(value) ? value : "#000000"}
          onChange={(e) => onChange(e.target.value.toUpperCase())}
        />
        <Input
          id={`color-${label.toLowerCase()}`}
          value={value}
          invalid={invalid}
          onChange={(e) => onChange(e.target.value)}
          placeholder="#RRGGBB"
          className="font-mono uppercase"
        />
      </div>
    </FormField>
  );
}

function FontField({
  label, options, value, onChange,
}: {
  label: string;
  options: string[];
  value: string;
  onChange: (v: string) => void;
}) {
  const isCustom = !options.includes(value);
  return (
    <FormField label={label} htmlFor={`font-${label.toLowerCase()}`}>
      <select
        id={`font-${label.toLowerCase()}`}
        className="h-10 w-full rounded-md border border-border-light bg-white-pure px-space-2 text-small text-onyx"
        value={isCustom ? "__custom" : value}
        onChange={(e) => {
          if (e.target.value === "__custom") {
            onChange("");
          } else {
            onChange(e.target.value);
          }
        }}
      >
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
        <option value="__custom">Custom...</option>
      </select>
      {isCustom && (
        <Input
          className="mt-space-1"
          placeholder="Custom font name"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
    </FormField>
  );
}

interface AssetUploadProps {
  label: string;
  helper: string;
  accept: string;
  currentName: string | null;
  currentUrl: string | null;
  uploader: (file: File) => Promise<BrandSettingsDto>;
  remover: () => Promise<BrandSettingsDto>;
  onChange: (next: BrandSettingsDto) => void;
}

function AssetUpload({
  label, helper, accept, currentName, currentUrl, uploader, remover, onChange,
}: AssetUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);

  const upload = useMutation({
    mutationFn: (file: File) => uploader(file),
    onSuccess: (dto) => {
      setError(null);
      onChange(dto);
    },
    onError: (e: Error) => setError(e.message ?? "Upload failed."),
  });

  const remove = useMutation({
    mutationFn: () => remover(),
    onSuccess: (dto) => {
      setError(null);
      onChange(dto);
    },
    onError: (e: Error) => setError(e.message ?? "Remove failed."),
  });

  return (
    <div>
      <Label className="mb-space-2 block">{label}</Label>
      <div className="flex items-start gap-space-3 rounded-md border border-dashed border-border-light p-space-3 bg-white-warm">
        <div className="h-16 w-16 shrink-0 rounded-md bg-white-pure border border-border-light flex items-center justify-center overflow-hidden">
          {currentUrl ? (
            <img src={currentUrl} alt={`${label} preview`} className="h-full w-full object-contain" />
          ) : (
            <ImageIcon size={20} strokeWidth={1.5} aria-hidden className="text-gray-mid" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-small text-onyx truncate">
            {currentName ?? <span className="text-gray-mid italic">No {label.toLowerCase()} uploaded</span>}
          </div>
          <p className="text-caption text-gray-mid mt-space-1">{helper}</p>
          <div className="flex flex-wrap items-center gap-space-2 mt-space-2">
            <input
              ref={inputRef}
              type="file"
              accept={accept}
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) upload.mutate(f);
                e.target.value = "";
              }}
            />
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={() => inputRef.current?.click()}
              loading={upload.isPending}
            >
              <Upload size={14} strokeWidth={1.5} aria-hidden />
              {currentName ? "Replace" : "Upload"}
            </Button>
            {currentName && (
              <Button
                type="button"
                size="sm"
                variant="text"
                onClick={() => remove.mutate()}
                loading={remove.isPending}
              >
                <Trash2 size={14} strokeWidth={1.5} aria-hidden /> Remove
              </Button>
            )}
          </div>
          {error && (
            <div className="mt-space-2 text-caption text-status-error" role="alert">
              {error}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function toPayload(dto: BrandSettingsDto): UpdateBrandSettingsPayload {
  return {
    companyName: dto.companyName,
    tagline: dto.tagline,
    primaryColor: dto.primaryColor,
    secondaryColor: dto.secondaryColor,
    accentColor: dto.accentColor,
    headerFont: dto.headerFont,
    bodyFont: dto.bodyFont,
    monoFont: dto.monoFont,
    companyAddress: dto.companyAddress,
    companyPhone: dto.companyPhone,
    companyEmail: dto.companyEmail,
    companyWebsite: dto.companyWebsite,
    legalFooter: dto.legalFooter,
    confidentialityNotice: dto.confidentialityNotice,
    brandVoiceDescription: dto.brandVoiceDescription,
    documentHeaderHtml: dto.documentHeaderHtml,
    customBrandingJson: dto.customBrandingJson,
    nativeAnalysisDocsEnabled: dto.nativeAnalysisDocsEnabled,
  };
}

function formatDate(iso: string, tz: string): string {
  return formatDateTimeInTz(iso, tz) || iso;
}
